import React, { memo, useMemo } from "react";
import { EdgeLabelRenderer, EdgeProps, getStraightPath } from "@xyflow/react";

type BrokenEdgeData = {
  segment?: "into" | "out";
  showBreak?: boolean;
};

const GAP_PX = 14;

function shortenLine(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  segment?: "into" | "out"
) {
  if (!segment) return { sx, sy, tx, ty };
  const dx = tx - sx;
  const dy = ty - sy;
  const len = Math.hypot(dx, dy);
  if (len <= 0.001) return { sx, sy, tx, ty };
  const ux = dx / len;
  const uy = dy / len;

  if (segment === "into") {
    // stop short of target
    return { sx, sy, tx: tx - ux * GAP_PX, ty: ty - uy * GAP_PX };
  }
  // start after source
  return { sx: sx + ux * GAP_PX, sy: sy + uy * GAP_PX, tx, ty };
}

function BrokenEdge(props: EdgeProps) {
  const data = (props.data || {}) as BrokenEdgeData;
  const { segment, showBreak } = data;

  const adjusted = useMemo(
    () =>
      shortenLine(
        props.sourceX,
        props.sourceY,
        props.targetX,
        props.targetY,
        segment
      ),
    [props.sourceX, props.sourceY, props.targetX, props.targetY, segment]
  );

  const [path] = getStraightPath({
    sourceX: adjusted.sx,
    sourceY: adjusted.sy,
    targetX: adjusted.tx,
    targetY: adjusted.ty,
  });

  return (
    <>
      <path
        id={props.id}
        className="react-flow__edge-path"
        d={path}
        style={props.style}
        markerEnd={props.markerEnd}
      />

      {showBreak && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${props.targetX}px, ${props.targetY}px)`,
              pointerEvents: "none",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 12,
              letterSpacing: 0.5,
              color: "rgba(255,255,255,0.92)",
              background: "rgba(239, 68, 68, 0.95)",
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: 6,
              padding: "1px 6px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
            }}
          >
            -/ /-
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export default memo(BrokenEdge);
